<template>
  <div class="seller-add-product-wrapper">
    <!-- Header Hero avec Glassmorphism -->
    <div class="product-hero">
      <div class="hero-content">
        <div class="hero-icon-wrapper">
          <v-icon size="32" color="white">mdi-store-plus</v-icon>
        </div>
        <div class="hero-text">
          <h1 class="text-h5 font-weight-bold white--text mb-1">
            {{ isEditMode ? 'Edit Product' : 'Add New Product' }}
          </h1>
          <p class="text-caption white--text mb-0" style="opacity: 0.9;">
            {{ isEditMode ? 'Update your listing details and media' : 'List your item in 4 simple steps' }}
          </p>
        </div>
      </div>
      <div class="hero-decoration"></div>
    </div>

    <!-- Stepper Amélioré avec Animation -->
    <div class="stepper-container">
      <div class="stepper-track">
        <div 
          class="stepper-progress" 
          :style="{ width: ((currentStep / (steps.length - 1)) * 100) + '%' }"
        ></div>
      </div>
      
      <div class="steps-row">
        <div
          v-for="(step, index) in steps"
          :key="index"
          class="step-item"
          :class="{ 
            'step-active': currentStep >= index,
            'step-current': currentStep === index,
            'step-completed': currentStep > index 
          }"
          @click="currentStep > index ? goToStep(index) : null"
        >
          <div class="step-circle">
            <v-scale-transition>
              <v-icon v-if="currentStep > index" size="16" color="white">mdi-check</v-icon>
              <v-icon v-else size="16" :color="currentStep >= index ? 'white' : 'grey'">
                {{ step.icon }}
              </v-icon>
            </v-scale-transition>
          </div>
          <span class="step-title">{{ step.label }}</span>
          <span class="step-subtitle" v-if="currentStep === index">Step {{ index + 1 }} of {{ steps.length }}</span>
        </div>
      </div>
    </div>

    <!-- Contenu Principal avec Transitions -->
    <v-container class="form-container pa-4 pa-md-6">
      <v-slide-x-transition mode="out-in">
        
        <!-- ÉTAPE 0: Informations de Base -->
        <v-card v-if="currentStep === 0" key="step0" flat class="step-card" elevation="2">
          <v-card-title class="step-header">
            <v-icon color="primary" class="mr-2">mdi-tag-outline</v-icon>
            <span class="title">Product Details</span>
          </v-card-title>
          
          <v-card-text class="pt-4">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="product_name"
                  ref="product_name"
                  filled
                  rounded
                  label="Product Name *"
                  placeholder="e.g., Wireless Bluetooth Headphones"
                  prepend-inner-icon="mdi-package-variant-closed"
                  :rules="[v => !!v || 'Required']"
                  class="mb-4 enhanced-field"
                  hide-details="auto"
                  @keyup.enter="focusNext('selectedCategory')"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="selectedCategory"
                  ref="selectedCategory"
                  :items="categoryItems"
                  item-text="name"
                  item-value="id"
                  filled
                  rounded
                  label="Category *"
                  placeholder="Select a category"
                  prepend-inner-icon="mdi-shape-outline"
                  hide-details="auto"
                  class="mb-4 enhanced-field"
                  menu-props="rounded, offset-y, max-height=300"
                >
                  <template v-slot:selection="{ item }">
                    <v-chip small color="primary lighten-5" text-color="primary">
                      {{ item.name }}
                    </v-chip>
                  </template>
                </v-select>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="supplier_contact"
                  ref="supplier_contact"
                  filled
                  rounded
                  type="tel"
                  label="WhatsApp Contact *"
                  placeholder="237 6XX XXX XXX"
                  prepend-inner-icon="mdi-whatsapp"
                  prefix="+"
                  hide-details="auto"
                  class="mb-4 enhanced-field"
                  hint="Customers will contact you here"
                  persistent-hint
                ></v-text-field>
              </v-col>

              <!-- KEYWORDS AMÉLIORÉ -->
              <v-col cols="12">
                <!-- Label avec compteur -->
                <div class="d-flex justify-space-between align-center mb-2">
                  <label class="text-subtitle-2 font-weight-medium">
                    <v-icon small color="primary" class="mr-1">mdi-tag-multiple</v-icon>
                    Keywords (Tags) *
                  </label>
                  <v-chip 
                    x-small 
                    :color="keywordsArray.length >= 5 ? 'error' : 'primary'" 
                    outlined
                  >
                    {{ keywordsArray.length }}/5
                  </v-chip>
                </div>

                <!-- Input field amélioré -->
                <div class="keywords-input-wrapper" :class="{ 'is-full': keywordsArray.length >= 5 }">
                  <v-combobox
                    v-model="keywordsArray"
                    ref="keywords_input"
                    filled
                    rounded
                    placeholder="Type a word, then press Enter"
                    prepend-inner-icon="mdi-tag-plus"
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    hide-details="auto"
                    class="enhanced-field keywords-field"
                    :disabled="keywordsArray.length >= 5"
                    :hint="keywordsHint"
                    persistent-hint
                    @keydown.enter.prevent="handleKeywordEnter"
                    @blur="handleKeywordBlur"
                  >
                    <!-- Chip personnalisé avec animation -->
                    <template v-slot:selection="{ attrs, item, parent, selected }">
                      <v-chip
                        v-bind="attrs"
                        :input-value="selected"
                        close
                        small
                        color="primary lighten-5"
                        text-color="primary"
                        class="keyword-chip"
                        @click:close="parent.selectItem(item)"
                      >
                        <v-icon left x-small>mdi-pound</v-icon>
                        {{ item }}
                      </v-chip>
                    </template>
                  </v-combobox>
                  
                  <!-- Overlay quand limite atteinte -->
                  <v-fade-transition>
                    <div v-if="keywordsArray.length >= 5" class="keywords-limit-overlay">
                      <v-chip color="success" small dark>
                        <v-icon left x-small>mdi-check</v-icon>
                        Maximum reached
                      </v-chip>
                    </div>
                  </v-fade-transition>
                </div>

                <!-- Suggestions rapides -->
                <div v-if="suggestedKeywords.length > 0 && keywordsArray.length < 5" class="keywords-suggestions mt-3">
                  <span class="text-caption grey--text mr-2">Suggestions:</span>
                  <v-chip
                    v-for="keyword in availableSuggestedKeywords"
                    :key="keyword"
                    x-small
                    outlined
                    color="grey darken-1"
                    class="mr-1 mb-1 suggestion-chip"
                    @click="addKeyword(keyword)"
                  >
                    <v-icon left x-small>mdi-plus</v-icon>
                    {{ keyword }}
                  </v-chip>
                </div>

                <!-- Exemples visuels -->
                <div v-if="keywordsArray.length === 0" class="keywords-examples mt-2">
                  <v-icon x-small color="grey lighten-1" class="mr-1">mdi-lightbulb-outline</v-icon>
                  <span class="text-caption grey--text">
                    Examples: 
                    <span class="example-tag" @click="addKeyword('Bluetooth')">Bluetooth</span>, 
                    <span class="example-tag" @click="addKeyword('Wireless')">Wireless</span>, 
                    <span class="example-tag" @click="addKeyword('Audio')">Audio</span>
                  </span>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- ÉTAPE 1: Prix et Stock -->
        <v-card v-else-if="currentStep === 1" key="step1" flat class="step-card" elevation="2">
          <v-card-title class="step-header">
            <v-icon color="primary" class="mr-2">mdi-cash-multiple</v-icon>
            <span class="title">Pricing Strategy</span>
          </v-card-title>

          <v-card-text class="pt-4">
            <div v-if="stepWarningMessage" class="step-warning mb-4">
              <v-icon small class="mr-1">mdi-alert</v-icon>
              <span>{{ stepWarningMessage }}</span>
            </div>
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="product_price"
                  filled
                  rounded
                  type="number"
                  label="Selling Price (XAF) *"
                  placeholder="0"
                  prepend-inner-icon="mdi-currency-usd"
                  suffix="XAF"
                  hide-details="auto"
                  class="mb-4 enhanced-field price-field"
                  background-color="success lighten-5"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  v-model="product_wholesale_price"
                  filled
                  rounded
                  type="number"
                  label="Cost Price (XAF) *"
                  placeholder="0"
                  prepend-inner-icon="mdi-currency-usd-off"
                  suffix="XAF"
                  hide-details="auto"
                  class="mb-4 enhanced-field price-field"
                  background-color="grey lighten-4"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  v-model="product_no_stock"
                  filled
                  rounded
                  type="number"
                  label="Stock Quantity *"
                  placeholder="0"
                  prepend-inner-icon="mdi-package-variant-closed"
                  suffix="units"
                  hide-details="auto"
                  class="mb-4 enhanced-field"
                ></v-text-field>
              </v-col>

              <!-- Calculateur de Marge en Temps Réel -->
              <v-col cols="12" v-if="margin > 0">
                <v-expand-transition>
                  <v-alert
                    prominent
                    :type="marginPercent >= 20 ? 'success' : 'warning'"
                    border="left"
                    colored-border
                    elevation="2"
                    class="profit-alert"
                  >
                    <div class="d-flex align-center justify-space-between">
                      <div>
                        <div class="text-h6 font-weight-bold mb-1">
                          {{ margin }} XAF profit per unit
                        </div>
                        <div class="text-body-2">
                          Margin: <v-chip x-small :color="marginPercent >= 20 ? 'success' : 'warning'" dark>{{ marginPercent }}%</v-chip>
                          <span v-if="marginPercent < 20" class="ml-2 warning--text">Consider increasing your price</span>
                          <span v-else class="ml-2 success--text">Great margin!</span>
                        </div>
                      </div>
                      <v-icon size="48" :color="marginPercent >= 20 ? 'success' : 'warning'">
                        {{ marginPercent >= 20 ? 'mdi-trending-up' : 'mdi-alert-circle-outline' }}
                      </v-icon>
                    </div>
                  </v-alert>
                </v-expand-transition>
              </v-col>

              <v-col cols="12">
                <v-alert
                  dense
                  outlined
                  type="info"
                  class="commission-alert"
                >
                  <div class="d-flex align-center justify-space-between flex-wrap">
                    <div>
                      <div class="font-weight-bold mb-1">Pettycash commission</div>
                      <div v-if="commissionConfig" class="text-caption">
                        {{ commissionLabel }} - deducted from product price (excluding delivery).
                      </div>
                      <div v-else class="text-caption grey--text">
                        Loading commission configuration...
                      </div>
                      <div v-if="commissionConfig" class="text-caption mt-1">
                        {{ commissionPriceLabel }}: commission {{ commissionAmountPerUnit }} XAF,
                        you receive {{ sellerNetPerUnit }} XAF.
                      </div>
                    </div>
                    <v-btn
                      small
                      text
                      color="primary"
                      :loading="commissionLoading"
                      @click="loadCommissionConfig"
                    >
                      Refresh
                    </v-btn>
                  </div>
                </v-alert>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- ÉTAPE 2: Médias avec Drag & Drop -->
        <v-card v-else-if="currentStep === 2" key="step2" flat class="step-card" elevation="2">
          <v-card-title class="step-header">
            <v-icon color="primary" class="mr-2">mdi-image-multiple</v-icon>
            <span class="title">Product Media</span>
          </v-card-title>

          <v-card-text class="pt-4">
            <div v-if="stepWarningMessage" class="step-warning mb-4">
              <v-icon small class="mr-1">mdi-alert</v-icon>
              <span>{{ stepWarningMessage }}</span>
            </div>
            <!-- Upload Zone Cover -->
            <div 
              class="upload-zone mb-6"
              :class="{ 'upload-active': dragover }"
              @dragover.prevent="dragover = true"
              @dragleave.prevent="dragover = false"
              @drop.prevent="handleCoverDrop"
              @click="$refs.fileInput.click()"
            >
              <input 
                ref="fileInput"
                type="file" 
                accept="image/*" 
                hidden 
                @change="onCoverChange($event.target.files[0])"
              >
              
              <div v-if="!coverPreview && !existingCoverUrl" class="upload-placeholder text-center pa-8">
                <v-icon size="64" color="primary lighten-2" class="mb-4">mdi-cloud-upload-outline</v-icon>
                <div class="text-h6 grey--text text--darken-1 mb-2">Drop cover image here</div>
                <div class="text-body-2 grey--text">or click to browse</div>
                <div class="text-caption grey--text mt-2">Recommended: 800x800px, JPG or PNG</div>
              </div>

              <div v-else class="cover-preview-container">
                <v-img :src="coverPreview || existingCoverUrl" height="300" contain class="rounded-lg"></v-img>
                <v-btn 
                  v-if="coverPreview"
                  fab 
                  small 
                  dark 
                  color="error" 
                  class="remove-image-btn"
                  @click.stop="removeCover"
                >
                  <v-icon>mdi-close</v-icon>
                </v-btn>
                <v-chip color="primary" dark small class="cover-label">
                  {{ coverPreview ? 'New Cover' : 'Current Cover' }}
                </v-chip>
              </div>
            </div>
            <div v-if="isEditMode && existingCoverUrl && !coverPreview"
              class="text-caption grey--text text--darken-1 mb-4">
              Current cover will be kept unless you upload a new one.
            </div>

            <!-- Upload Images Additionnelles -->
            <div class="additional-images-section">
              <div class="d-flex align-center justify-space-between mb-3">
                <span class="subtitle-2 font-weight-bold grey--text text--darken-2">
                  Additional Images ({{ product_images.length }})
                </span>
                <v-btn 
                  text 
                  small 
                  color="primary" 
                  @click="$refs.galleryInput.click()"
                  :disabled="product_images.length >= 5"
                >
                  <v-icon left small>mdi-plus</v-icon>
                  Add Photos
                </v-btn>
                <input 
                  ref="galleryInput"
                  type="file" 
                  accept="image/*" 
                  multiple 
                  hidden 
                  @change="handleGalleryUpload"
                >
              </div>

              <div v-if="existingGallery.length" class="existing-gallery mb-4">
                <div class="subtitle-2 font-weight-bold grey--text text--darken-2 mb-2">
                  Existing Images ({{ existingGallery.length }})
                </div>
                <div class="gallery-grid">
                  <div
                    v-for="(img, idx) in existingGallery"
                    :key="'existing-'+idx"
                    class="gallery-item"
                  >
                    <v-img :src="img.get_image || img" height="100" class="rounded-lg"></v-img>
                  </div>
                </div>
              </div>

              <div class="gallery-grid" v-if="galleryPreviews.length > 0">
                <div 
                  v-for="(img, idx) in galleryPreviews" 
                  :key="idx"
                  class="gallery-item"
                >
                  <v-img :src="img" height="100" class="rounded-lg"></v-img>
                  <v-btn icon x-small dark color="error" class="remove-gallery-btn" @click="removeGalleryImage(idx)">
                    <v-icon x-small>mdi-close</v-icon>
                  </v-btn>
                </div>
                <div 
                  v-if="product_images.length < 5"
                  class="gallery-item add-more"
                  @click="$refs.galleryInput.click()"
                >
                  <v-icon size="32" color="grey lighten-1">mdi-plus</v-icon>
                </div>
              </div>
            </div>

            <!-- Video Upload -->
            <v-file-input
              v-model="product_video"
              filled
              rounded
              accept="video/*"
              label="Product Video (Optional)"
              prepend-icon="mdi-video-outline"
              truncate-length="25"
              class="mt-6 enhanced-field"
              hint="Short demo videos increase sales by 40%"
              persistent-hint
            >
              <template v-slot:selection="{ text }">
                <v-chip small color="primary" dark>
                  <v-icon left x-small>mdi-video</v-icon>
                  {{ text }}
                </v-chip>
              </template>
            </v-file-input>
          </v-card-text>
        </v-card>

        <!-- ÉTAPE 3: Description avec Prévisualisation -->
        <v-card v-else-if="currentStep === 3" key="step3" flat class="step-card" elevation="2">
          <v-card-title class="step-header">
            <v-icon color="primary" class="mr-2">mdi-text-box-edit</v-icon>
            <span class="title">Description</span>
            <v-spacer></v-spacer>
            <v-chip small outlined color="grey">
              {{ descriptionLength }} chars
            </v-chip>
          </v-card-title>

          <v-card-text class="pt-4">
            <v-row>
              <v-col cols="12" md="8">
                <!-- Éditeur Amélioré -->
                <div class="rich-editor-wrapper">
                  <div class="editor-toolbar">
                    <v-btn-toggle v-model="toggleFormat" multiple dense background-color="transparent">
                      <v-btn small text @click="execCmd('bold')" title="Bold">
                        <v-icon small>mdi-format-bold</v-icon>
                      </v-btn>
                      <v-btn small text @click="execCmd('italic')" title="Italic">
                        <v-icon small>mdi-format-italic</v-icon>
                      </v-btn>
                      <v-btn small text @click="execCmd('underline')" title="Underline">
                        <v-icon small>mdi-format-underline</v-icon>
                      </v-btn>
                      <v-divider vertical class="mx-1"></v-divider>
                      <v-btn small text @click="execCmd('insertUnorderedList')" title="Bullet List">
                        <v-icon small>mdi-format-list-bulleted</v-icon>
                      </v-btn>
                      <v-btn small text @click="execCmd('insertOrderedList')" title="Numbered List">
                        <v-icon small>mdi-format-list-numbered</v-icon>
                      </v-btn>
                    </v-btn-toggle>
                  </div>
                  
                  <div
                    ref="descriptionEditor"
                    class="rich-editor"
                    contenteditable="true"
                    @input="onDescriptionInput"
                    placeholder="Describe your product features, benefits, and specifications..."
                  ></div>
                  
                  <div class="editor-footer d-flex justify-space-between align-center px-3 py-2">
                    <span class="caption grey--text">Tip: Good descriptions increase conversion</span>
                    <span class="caption" :class="descriptionLength < 100 ? 'error--text' : 'success--text'">
                      {{ descriptionLength < 100 ? 'Min 100 characters recommended' : '✓ Good length' }}
                    </span>
                  </div>
                </div>
              </v-col>

              <v-col cols="12" md="4">
                <!-- Prévisualisation Live -->
                <div class="preview-panel">
                  <div class="preview-title mb-2">Preview</div>
                  <div class="preview-content" v-html="product_description || '<span class=\'grey--text\'>Your description will appear here...</span>'"></div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

      </v-slide-x-transition>

      <!-- Navigation Fixe -->
      <div class="action-bar">
        <v-btn
          text
          large
          rounded
          class="mr-4"
          :disabled="currentStep === 0"
          @click="currentStep--"
        >
          <v-icon left>mdi-arrow-left</v-icon>
          Back
        </v-btn>

        <v-spacer></v-spacer>

        <v-btn
          v-if="currentStep < steps.length - 1"
          color="primary"
          dark
          large
          rounded
          elevation="2"
          min-width="140"
          @click="nextStep"
          :disabled="!isStepValid"
        >
          Continue
          <v-icon right class="ml-2">mdi-arrow-right</v-icon>
        </v-btn>

        <v-btn
          v-else
          color="success"
          dark
          large
          rounded
          elevation="2"
          min-width="180"
          :loading="loading"
          :disabled="!canSubmit"
          @click="addProduct"
        >
          <v-icon left class="mr-2">mdi-check-circle</v-icon>
          {{ isEditMode ? 'Update Product' : 'Publish Product' }}
        </v-btn>
      </div>
    </v-container>

    <!-- Dialog de Confirmation -->
    <v-dialog v-model="showConfirm" max-width="400" persistent>
      <v-card class="pa-4 text-center">
        <v-icon size="64" color="success" class="mb-4">mdi-check-circle-outline</v-icon>
        <v-card-title class="justify-center headline mb-2">
          {{ isEditMode ? 'Product Updated!' : 'Product Published!' }}
        </v-card-title>
        <v-card-text class="text-body-1 grey--text">
          {{ isEditMode ? 'Your changes are now live on the marketplace.' : 'Your product is now live on the marketplace.' }}
        </v-card-text>
        <v-card-actions class="justify-center pt-4">
          <v-btn color="primary" dark rounded @click="goToProduct">
            View Product
          </v-btn>
          <v-btn text rounded @click="addAnother">
            Add Another
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'SellerAddProduct',

  data() {
    return {
      currentStep: 0,
      loading: false,
      dragover: false,
      coverPreview: null,
      galleryPreviews: [],
      showConfirm: false,
      newProductId: null,
      toggleFormat: [],
      isEditMode: false,
      editProductId: null,
      existingCoverUrl: null,
      existingGallery: [],
      existingVideoUrl: null,

      // Commission
      commissionConfig: null,
      commissionLoading: false,
      commissionError: null,
      commissionSamplePrice: 10000,

      // Nouveau: Suggestions de mots-clés
      suggestedKeywords: ['New', 'Trending', 'Best Seller', 'Quality', 'Discount'],

      // Form Data
      product_name: '',
      supplier_contact: '237',
      selectedCategory: null,
      product_description: '',
      product_video: null,
      product_images: [],
      product_cover_image: null,
      product_no_stock: '',
      product_wholesale_price: '',
      product_price: '',
      keywordsArray: [],
      categoryItems: [],

      steps: [
        { label: 'Basic Info', icon: 'mdi-information' },
        { label: 'Pricing', icon: 'mdi-cash' },
        { label: 'Media', icon: 'mdi-image' },
        { label: 'Description', icon: 'mdi-text' },
      ],
    }
  },

  computed: {
    margin() {
      const price = parseInt(this.product_price) || 0
      const cost = parseInt(this.product_wholesale_price) || 0
      return price - cost
    },
    marginPercent() {
      const cost = parseInt(this.product_wholesale_price) || 0
      if (!cost) return 0
      return Math.round((this.margin / cost) * 100)
    },
    commissionType() {
      return this.commissionConfig?.commission_type || 'PERCENT'
    },
    commissionValue() {
      const value = this.commissionConfig?.value
      const parsed = parseFloat(value)
      return Number.isFinite(parsed) ? parsed : 0
    },
    commissionLabel() {
      if (!this.commissionConfig) return 'Loading...'
      if (this.commissionType === 'FIXED') {
        return `${this.commissionValue} XAF per item`
      }
      return `${this.commissionValue}% per item`
    },
    commissionBasePrice() {
      const price = parseFloat(this.product_price)
      if (Number.isFinite(price) && price > 0) return price
      return this.commissionSamplePrice
    },
    commissionAmountPerUnit() {
      if (!this.commissionConfig) return 0
      if (this.commissionType === 'FIXED') return Math.round(this.commissionValue)
      return Math.round((this.commissionBasePrice * this.commissionValue) / 100)
    },
    sellerNetPerUnit() {
      const net = this.commissionBasePrice - this.commissionAmountPerUnit
      return net < 0 ? 0 : Math.round(net)
    },
    commissionPriceLabel() {
      if (!this.product_price) return `Example on ${this.commissionBasePrice} XAF`
      return 'Based on your price'
    },
    key_words() {
      return this.keywordsArray.join(', ')
    },
    hasCover() {
      return !!(this.coverPreview || this.existingCoverUrl || this.product_cover_image)
    },
    descriptionLength() {
      return this.product_description.replace(/<[^>]+>/g, '').length
    },
    // Nouveau: Keywords filtrés disponibles
    availableSuggestedKeywords() {
      return this.suggestedKeywords.filter(k => !this.keywordsArray.includes(k))
    },
    // Nouveau: Hint dynamique pour keywords
    keywordsHint() {
      if (this.keywordsArray.length >= 5) {
        return 'Limit reached. Remove a tag to add more.'
      }
      if (this.keywordsArray.length === 0) {
        return 'Press Enter after each word. You can also paste multiple tags separated by commas.'
      }
      return `${5 - this.keywordsArray.length} tag${5 - this.keywordsArray.length > 1 ? 's' : ''} remaining`
    },
    stepWarningMessage() {
      if (this.currentStep === 1) {
        const price = parseFloat(this.product_price)
        const cost = parseFloat(this.product_wholesale_price)
        const stock = parseInt(this.product_no_stock)
        if (!price || !cost || !stock) {
          return 'Enter values greater than 0 for price and stock to continue.'
        }
        if (price <= cost) {
          return 'Selling price must be higher than cost price.'
        }
      }
      if (this.currentStep === 2) {
        if (!this.hasCover) return 'Add a cover image to continue.'
      }
      return ''
    },
    isStepValid() {
      // Validation par étape
      if (this.currentStep === 0) {
        return this.product_name && this.selectedCategory && this.supplier_contact && this.keywordsArray.length > 0
      }
      if (this.currentStep === 1) {
        return this.product_price && this.product_wholesale_price && this.product_no_stock
      }
      if (this.currentStep === 2) {
        return this.hasCover
      }
      return true
    },
    canSubmit() {
      return this.product_description.replace(/<[^>]+>/g, '').trim().length > 10
    }
  },

  watch: {
    keywordsArray(val) {
      // Limite à 5 keywords
      if (val.length > 5) {
        this.keywordsArray = val.slice(0, 5)
        this.$store.commit('setSnackBarMessage', 'Maximum 5 keywords allowed')
        this.$store.commit('setSnackBarColor', 'warning')
        this.$store.commit('activateSnackBar', true)
      }
    },
    // Nouveau: Mettre à jour les suggestions selon la catégorie
    selectedCategory(newVal) {
      if (newVal) {
        this.updateSuggestedKeywords(newVal)
      }
    }
  },

  mounted() {
    document.title = 'Add Product | Seller Dashboard'
    this.loadCommissionConfig()
    this.getProductCategories().then(() => {
      this.initEditMode()
    })
  },

  methods: {
    // NOUVELLES MÉTHODES POUR KEYWORDS
    handleKeywordEnter(e) {
      // Empêche la soumission du formulaire si dans un form
      e.preventDefault()
      
      const input = this.$refs.keywords_input
      const value = input?.$refs?.input?.value || input?.internalSearch
      
      if (value && value.trim()) {
        // Gère la séparation par virgule/espace si l'utilisateur colle du texte
        const separators = /[,\s]+/
        const newTags = value.split(separators)
          .map(t => t.trim())
          .filter(t => t && !this.keywordsArray.includes(t))
        
        const availableSlots = 5 - this.keywordsArray.length
        const tagsToAdd = newTags.slice(0, availableSlots)
        
        if (tagsToAdd.length > 0) {
          this.keywordsArray = [...this.keywordsArray, ...tagsToAdd]
          // Feedback visuel seulement si plusieurs tags ajoutés d'un coup
          if (tagsToAdd.length > 1) {
            this.$store.commit('setSnackBarMessage', `${tagsToAdd.length} tags added`)
            this.$store.commit('setSnackBarColor', 'success')
            this.$store.commit('activateSnackBar', true)
          }
        }
        
        // Reset l'input
        if (input) {
          input.internalSearch = ''
          if (input.$refs.input) input.$refs.input.value = ''
        }
      }
    },

    handleKeywordBlur() {
      // Si l'utilisateur quitte le champ avec du texte non validé
      const input = this.$refs.keywords_input
      const value = input?.$refs?.input?.value || input?.internalSearch
      
      if (value && value.trim() && this.keywordsArray.length < 5) {
        // Gère aussi les séparateurs ici
        if (value.includes(',') || value.includes(' ')) {
          this.handleKeywordEnter({ preventDefault: () => {} })
        } else {
          this.addKeyword(value.trim())
        }
        if (input) {
          input.internalSearch = ''
          if (input.$refs.input) input.$refs.input.value = ''
        }
      }
    },

    addKeyword(keyword) {
      if (this.keywordsArray.length >= 5) return
      const cleanKeyword = keyword.trim()
      if (cleanKeyword && !this.keywordsArray.includes(cleanKeyword)) {
        this.keywordsArray.push(cleanKeyword)
      }
    },

    updateSuggestedKeywords(categoryId) {
      // Suggestions contextuelles basées sur la catégorie
      const suggestionsMap = {
        // Électronique
        1: ['Electronic', 'Gadget', 'Tech', 'Digital', 'Smart'],
        // Mode/Vêtements  
        2: ['Fashion', 'Trendy', 'Casual', 'Elegant', 'Vintage'],
        // Maison/Décoration
        3: ['Home', 'Decor', 'Interior', 'Design', 'Comfort'],
        // Sports
        4: ['Sport', 'Fitness', 'Outdoor', 'Training', 'Performance'],
        // Beauté
        5: ['Beauty', 'Care', 'Organic', 'Natural', 'Skincare'],
        // Default
      }
      
      const categorySuggestions = suggestionsMap[categoryId] || ['New', 'Popular', 'Quality', 'Best Price', 'Original']
      this.suggestedKeywords = [...new Set([...categorySuggestions, 'New', 'Hot'])]
    },

    // MÉTHODES EXISTANTES
    async loadCommissionConfig() {
      this.commissionLoading = true
      try {
        const res = await axios.get('/api/v1/market_place/commission/config/')
        this.commissionConfig = res.data
      } catch (err) {
        this.commissionConfig = null
        this.handleError(err)
      } finally {
        this.commissionLoading = false
      }
    },

    async getProductCategories() {
      try {
        const res = await axios.get('/api/v1/manager/market_place/get/product/categories/')
        this.categoryItems = res.data
        return res.data
      } catch (err) {
        this.handleError(err)
        return []
      }
    },

    async initEditMode() {
      const editId = this.$route.query.edit
      if (!editId) return
      const parsedId = parseInt(editId)
      if (!parsedId) return

      this.isEditMode = true
      this.editProductId = parsedId
      document.title = 'Edit Product | Seller Dashboard'
      await this.loadProductForEdit(parsedId)
    },

    async loadProductForEdit(productId) {
      this.loading = true
      try {
        const res = await axios.get(`/api/v1/market_place/seller/products/${productId}/detail/`)
        const data = res.data || {}

        this.product_name = data.name || ''
        this.supplier_contact = data.supplier_contact || '237'
        this.selectedCategory = data.category_id || null
        this.product_description = data.description || ''
        this.product_no_stock = data.quantity || ''
        this.product_wholesale_price = data.wholesale_price || ''
        this.product_price = data.price || ''
        this.existingCoverUrl = data.get_image || null
        this.existingGallery = data.product_images || []
        if (Array.isArray(data.product_videos) && data.product_videos.length > 0) {
          this.existingVideoUrl = data.product_videos[0]?.get_video || null
        } else {
          this.existingVideoUrl = null
        }

        if (data.key_words) {
          this.keywordsArray = data.key_words
            .split(',')
            .map(s => s.trim())
            .filter(Boolean)
        }

        this.$nextTick(() => {
          if (this.$refs.descriptionEditor) {
            this.$refs.descriptionEditor.innerHTML = this.product_description || ''
          }
        })
      } catch (err) {
        this.handleError(err)
      } finally {
        this.loading = false
      }
    },

    goToStep(step) {
      this.currentStep = step
    },

    focusNext(refName) {
      this.$refs[refName]?.focus()
    },

    // Gestion Images
    handleCoverDrop(e) {
      this.dragover = false
      const file = e.dataTransfer.files[0]
      if (file && file.type.startsWith('image/')) {
        this.onCoverChange(file)
      }
    },

    onCoverChange(file) {
      if (!file) return
      this.product_cover_image = file
      this.coverPreview = URL.createObjectURL(file)
    },

    removeCover() {
      this.product_cover_image = null
      this.coverPreview = null
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },

    handleGalleryUpload(e) {
      const files = Array.from(e.target.files)
      const remainingSlots = 5 - this.product_images.length
      
      files.slice(0, remainingSlots).forEach(file => {
        this.product_images.push(file)
        this.galleryPreviews.push(URL.createObjectURL(file))
      })
    },

    removeGalleryImage(index) {
      this.product_images.splice(index, 1)
      this.galleryPreviews.splice(index, 1)
    },

    // Éditeur
    onDescriptionInput() {
      this.product_description = this.$refs.descriptionEditor.innerHTML
    },

    execCmd(command) {
      document.execCommand(command, false, null)
      this.$refs.descriptionEditor.focus()
      this.product_description = this.$refs.descriptionEditor.innerHTML
    },

    // Navigation
    nextStep() {
      if (this.currentStep === 0) {
        if (!this.product_name) return this.shakeField('product_name')
        if (!this.selectedCategory) return this.shakeField('selectedCategory')
        if (!this.supplier_contact) return this.shakeField('supplier_contact')
        if (this.keywordsArray.length === 0) {
          this.$store.commit('setSnackBarMessage', 'Add at least one keyword')
          this.$store.commit('setSnackBarColor', 'warning')
          this.$store.commit('activateSnackBar', true)
          this.$refs.keywords_input?.focus?.()
          return
        }
      }
      
      if (this.currentStep === 1) {
        if (!this.product_price || !this.product_wholesale_price) {
          return this.$store.commit('setSnackBarMessage', 'Please fill all price fields')
        }
        if (parseInt(this.product_price) <= parseInt(this.product_wholesale_price)) {
          return this.$store.commit('setSnackBarMessage', 'Selling price must be higher than cost price')
        }
      }

      if (this.currentStep === 2) {
        if (!this.hasCover) {
          return
        }
      }

      if (this.currentStep < this.steps.length - 1) {
        this.currentStep++
      }
    },

    shakeField(refName) {
      const field = this.$refs[refName]
      if (field) {
        field.focus()
        // Animation visuelle de shake via CSS class
        field.$el.classList.add('shake-animation')
        setTimeout(() => field.$el.classList.remove('shake-animation'), 500)
      }
    },

    async addProduct() {
      if (!this.canSubmit) return
      if (this.isEditMode) {
        return this.updateProduct()
      }

      this.loading = true

      const formData = new FormData()
      formData.append('product_name', this.product_name)
      formData.append('supplier_contact', this.supplier_contact)
      formData.append('product_cover_image', this.product_cover_image)
      formData.append('selectedCategory', this.selectedCategory)
      formData.append('product_no_stock', this.product_no_stock)
      formData.append('product_description', this.product_description)
      formData.append('key_words', this.key_words)
      formData.append('product_price', this.product_price)
      formData.append('product_wholesale_price', this.product_wholesale_price)

      if (this.product_video) {
        formData.append('product_video', this.product_video)
      }
      
      this.product_images.forEach((img, idx) => {
        formData.append(`product_images${idx}`, img)
      })
      formData.append('product_images_length', this.product_images.length)

      try {
        const res = await axios.post(
          '/api/v1/market_place/seller/products/add/',
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        )

        this.newProductId = res.data[0].product_id
        this.showConfirm = true

      } catch (err) {
        this.handleError(err)
      } finally {
        this.loading = false
      }
    },

    async updateProduct() {
      if (!this.canSubmit || !this.editProductId) return

      this.loading = true

      const formData = new FormData()
      formData.append('product_name', this.product_name)
      formData.append('supplier_contact', this.supplier_contact)
      formData.append('selectedCategory', this.selectedCategory)
      formData.append('product_no_stock', this.product_no_stock)
      formData.append('product_description', this.product_description)
      formData.append('key_words', this.key_words)
      formData.append('product_price', this.product_price)
      formData.append('product_wholesale_price', this.product_wholesale_price)

      if (this.product_cover_image) {
        formData.append('product_cover_image', this.product_cover_image)
      }
      if (this.product_video) {
        formData.append('product_video', this.product_video)
      }
      this.product_images.forEach((img, idx) => {
        formData.append(`product_images${idx}`, img)
      })
      formData.append('product_images_length', this.product_images.length)

      try {
        const res = await axios.post(
          `/api/v1/market_place/seller/products/${this.editProductId}/edit/`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        )

        this.newProductId = this.editProductId
        this.showConfirm = true
        if (res.data?.message) {
          this.$store.commit('setSnackBarMessage', res.data.message)
          this.$store.commit('setSnackBarColor', 'success')
          this.$store.commit('activateSnackBar', true)
        }
      } catch (err) {
        this.handleError(err)
      } finally {
        this.loading = false
      }
    },

    goToProduct() {
      this.$router.push(`/market_place/items/${this.newProductId}`)
    },

    addAnother() {
      if (this.isEditMode) {
        this.$router.push({ name: 'business_seller.products' })
      } else {
        this.$router.go()
      }
    },

    handleError(err) {
      const msg = err.response?.data?.error || err.response?.data?.detail || 'An error occurred'
      this.$store.commit('setSnackBarMessage', msg)
      this.$store.commit('setSnackBarColor', 'error')
      this.$store.commit('activateSnackBar', true)
    }
  }
}
</script>

<style scoped>
.seller-add-product-wrapper {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding-bottom: 100px;
}

/* Header Hero */
.product-hero {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 40px 24px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
}

.hero-content {
  display: flex;
  align-items: center;
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
}

.hero-icon-wrapper {
  width: 64px;
  height: 64px;
  background: rgba(255,255,255,0.2);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.1);
}

.hero-decoration {
  position: absolute;
  top: -50%;
  right: -10%;
  width: 300px;
  height: 300px;
  background: rgba(255,255,255,0.1);
  border-radius: 50%;
  z-index: 1;
}

/* Stepper Amélioré */
.stepper-container {
  max-width: 800px;
  margin: -30px auto 0;
  padding: 0 20px;
  position: relative;
  z-index: 3;
}

.stepper-track {
  height: 4px;
  background: #e0e0e0;
  border-radius: 2px;
  margin: 0 40px;
  position: relative;
  top: 20px;
  z-index: 0;
}

.stepper-progress {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  border-radius: 2px;
  transition: width 0.4s ease;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
  position: relative;
  z-index: 0;
}

.steps-row {
  display: flex;
  justify-content: space-between;
  position: relative;
  z-index: 1;
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.step-circle {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: white;
  border: 3px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.step-active .step-circle {
  border-color: #667eea;
  background: #667eea;
  transform: scale(1.1);
}

.step-current .step-circle {
  box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.2);
}

.step-completed .step-circle {
  background: #4caf50;
  border-color: #4caf50;
}

.step-title {
  margin-top: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #9e9e9e;
  transition: color 0.3s;
}

.step-active .step-title {
  color: #667eea;
}

.step-completed .step-title {
  color: #4caf50;
}

.step-subtitle {
  font-size: 11px;
  color: #9e9e9e;
  margin-top: 2px;
}

/* Form Container */
.form-container {
  max-width: 800px;
  margin: 30px auto;
}

.step-card {
  border-radius: 16px !important;
  overflow: hidden;
  background: white;
  margin-bottom: 80px;
}

.step-header {
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
  padding: 20px 24px;
}

/* Enhanced Fields */
.enhanced-field >>> .v-input__control {
  border-radius: 12px;
}

.enhanced-field >>> .v-input__slot {
  min-height: 56px !important;
  border-radius: 12px !important;
  transition: all 0.3s ease !important;
}

.enhanced-field >>> .v-input__slot:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
}

.price-field >>> .v-input__slot {
  background: linear-gradient(135deg, #f3f0ff 0%, #e0e7ff 100%) !important;
}

/* KEYWORDS STYLES AMÉLIORÉS */
.keywords-input-wrapper {
  position: relative;
  transition: all 0.3s ease;
}

.keywords-input-wrapper.is-full {
  opacity: 0.9;
}

.keywords-limit-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  pointer-events: none;
  background: rgba(255,255,255,0.9);
  padding: 8px 16px;
  border-radius: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.keywords-field {
  transition: all 0.3s ease;
}

.keywords-field.v-input--is-disabled >>> .v-input__slot {
  background-color: #f5f5f5 !important;
  border-radius: 12px;
}

.keyword-chip {
  font-weight: 500;
  animation: chipEnter 0.3s ease;
}

@keyframes chipEnter {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.keywords-suggestions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}

.suggestion-chip {
  cursor: pointer;
  transition: all 0.2s ease !important;
}

.suggestion-chip:hover {
  background-color: var(--v-primary-base) !important;
  color: white !important;
  border-color: var(--v-primary-base) !important;
  transform: translateY(-1px);
}

.keywords-examples {
  display: flex;
  align-items: center;
}

.example-tag {
  color: var(--v-primary-base);
  cursor: pointer;
  text-decoration: underline;
  text-decoration-style: dotted;
  margin: 0 4px;
  font-weight: 500;
  transition: all 0.2s;
}

.example-tag:hover {
  font-weight: 700;
  color: var(--v-primary-darken1);
}

/* Profit Alert */
.profit-alert {
  border-radius: 12px !important;
  background: white !important;
}

/* Commission Alert */
.commission-alert {
  border-radius: 12px !important;
  background: #f8fbff !important;
}

/* Upload Zone */
.upload-zone {
  border: 2px dashed #cbd5e0;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f7fafc;
  overflow: hidden;
  position: relative;
}

.upload-zone:hover, .upload-active {
  border-color: #667eea;
  background: #f0f4ff;
  transform: scale(1.01);
}

.upload-placeholder {
  transition: transform 0.3s ease;
}

.upload-zone:hover .upload-placeholder {
  transform: translateY(-5px);
}

.cover-preview-container {
  position: relative;
  padding: 16px;
}

.remove-image-btn {
  position: absolute !important;
  top: 24px;
  right: 24px;
  z-index: 5;
}

.cover-label {
  position: absolute;
  bottom: 24px;
  left: 24px;
}

/* Gallery Grid */
.gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 12px;
}

.existing-gallery .gallery-item {
  opacity: 0.9;
}

.gallery-item {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  aspect-ratio: 1;
  cursor: pointer;
  transition: transform 0.2s;
}

.gallery-item:hover {
  transform: scale(1.05);
}

.add-more {
  border: 2px dashed #cbd5e0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f7fafc;
}

.add-more:hover {
  border-color: #667eea;
  background: #f0f4ff;
}

.remove-gallery-btn {
  position: absolute !important;
  top: 4px;
  right: 4px;
  z-index: 5;
}

/* Rich Editor */
.rich-editor-wrapper {
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  overflow: hidden;
  background: white;
}

.editor-toolbar {
  background: #f8f9fa;
  padding: 8px;
  border-bottom: 1px solid #e0e0e0;
}

.rich-editor {
  min-height: 200px;
  padding: 16px;
  outline: none;
  font-size: 15px;
  line-height: 1.6;
}

.rich-editor:empty:before {
  content: attr(placeholder);
  color: #a0aec0;
  pointer-events: none;
}

.editor-footer {
  background: #f8f9fa;
  border-top: 1px solid #e0e0e0;
}

/* Preview Panel */
.preview-panel {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #e0e0e0;
  height: 100%;
  min-height: 280px;
}

.preview-title {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #718096;
  font-weight: 600;
}

.preview-content {
  color: #2d3748;
  line-height: 1.6;
  font-size: 14px;
}

/* Step Warning */
.step-warning {
  border: 1px solid #f59e0b;
  background: #fffbeb;
  color: #92400e;
  padding: 10px 12px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
}

/* Action Bar */
.action-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  padding: 16px 24px;
  box-shadow: 0 -4px 20px rgba(0,0,0,0.08);
  display: flex;
  align-items: center;
  z-index: 100;
  max-width: 800px;
  margin: 0 auto;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
}

/* Animations */
.shake-animation {
  animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) both;
}

@keyframes shake {
  10%, 90% { transform: translate3d(-1px, 0, 0); }
  20%, 80% { transform: translate3d(2px, 0, 0); }
  30%, 50%, 70% { transform: translate3d(-4px, 0, 0); }
  40%, 60% { transform: translate3d(4px, 0, 0); }
}

/* Responsive */
@media (max-width: 768px) {
  .product-hero {
    padding: 24px 16px;
  }
  
  .hero-icon-wrapper {
    width: 48px;
    height: 48px;
  }
  
  .step-title {
    display: none;
  }
  
  .step-circle {
    width: 36px;
    height: 36px;
  }
}
</style>